import React, { useEffect, useMemo, useState } from "react";

import {
  InputAdornment,
  TextField,
  TextFieldProps,
  Theme,
} from "@mui/material";
import {
  AsYouType,
  CountryCode,
  getExampleNumber,
  PhoneNumber,
} from "libphonenumber-js";
import examples from "libphonenumber-js/examples.mobile.json";
import { makeStyles, withStyles } from "tss-react/mui";

import { maybeCall } from "@/utils/functions";

import MobileInputCountryPicker from "./MobileInputCountryPicker";

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    position: "relative",
  },

  error: {
    fontSize: "12px",
    textAlign: "center",
    color: "#963832",
    position: "absolute",
  },

  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },

  selectEmpty: {
    marginTop: theme.spacing(2),
  },
}));

const StyledTextField = withStyles(TextField, {
  root: {
    width: "100%",
    marginTop: "2px",

    /*Hide arrows */
    /* Chrome, Safari, Edge, Opera */
    "& input::-webkit-inner-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
    "& input::-webkit-outer-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },

    /* Firefox */
    "& input[type=number]": {
      "-moz-appearance": "textfield",
    },
  },
});

type MobileInputProps = TextFieldProps & {
  numberType?: "mobile" | "telephone";
};

const MobileInput: React.FC<MobileInputProps> = ({
  numberType = "mobile",
  value,
  onChange,
  ...textFieldProps
}) => {
  const { classes: myClasses } = useStyles();

  const initial = parseIncompleteNumber((value as string) ?? "", "PH");
  const initialPhoneNumber = (() => {
    if (numberType === "mobile") {
      return initial?.format("E.164");
    }
    return initial?.formatNational();
  })();

  const [countryCode, setCountryCode] = useState<CountryCode>(
    initial?.country ?? "PH"
  );

  const [input, setInput] = useState(initialPhoneNumber ?? "");

  const handleChangeInput = (value: string) => {
    const parsed = parseIncompleteNumber(value, countryCode);
    if (numberType === "mobile") {
      setInput(parsed?.format("E.164") ?? "");
    } else {
      setInput(parsed?.format("NATIONAL") ?? "");
    }
  };

  const handleChangeCountry = (value: CountryCode) => {
    const parsedE164 = parseIncompleteNumber(input);
    const parsed = parseIncompleteNumber(
      parsedE164?.formatNational() ?? "",
      value
    );
    setCountryCode(value);
    if (numberType === "mobile") {
      setInput(parsed?.format("E.164") ?? "");
    }
  };

  useEffect(() => {
    //always return E.164 format
    const parsed: PhoneNumber | undefined = parseIncompleteNumber(
      input ?? "",
      countryCode
    );
    maybeCall(onChange, (parsed?.format("E.164") ?? "") as any);
  }, [input]);

  const exampleMobileNumber = getExampleNumber(
    countryCode,
    examples
  )?.formatInternational();

  const example = numberType === "mobile" ? exampleMobileNumber : "";

  const asYouType = new AsYouType(countryCode).input(input);

  const InputProps = useMemo(() => {
    return {
      startAdornment: (
        <InputAdornment position="start">
          <MobileInputCountryPicker
            value={countryCode}
            onChange={(newCountry) => handleChangeCountry(newCountry)}
          />
        </InputAdornment>
      ),
      ...textFieldProps.InputProps,
    };
  }, [countryCode, textFieldProps.InputProps]);

  return (
    <StyledTextField
      {...textFieldProps}
      value={asYouType}
      placeholder={example}
      onChange={(event) => handleChangeInput(event.target.value)}
      InputProps={InputProps}
    />
  );
};

function parseIncompleteNumber(
  text: string,
  defaultCountryCode?: CountryCode
): PhoneNumber | undefined {
  const asYouType = new AsYouType(defaultCountryCode);
  asYouType.input(text);
  return asYouType.getNumber();
}

export default MobileInput;
